<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const isLoaded = ref(false)

// Simulación del Agente Inteligente (Backend)
const terminalSteps = ref([])
const processSimulation = [
  { text: 'Conectando RAG con base de datos...', type: 'system' },
  { text: 'Catálogo sincronizado. (2,450 SKUs)', type: 'success' },
  { text: 'Mensaje entrante (WhatsApp: +505 8888-0000)', type: 'alert' },
  { text: 'Analizando intención de compra...', type: 'process' },
  { text: 'Cotización generada (0.4s de latencia).', type: 'success' },
  { text: 'Extrayendo variable: {Nombre: "Carlos Mendoza"}', type: 'extract' },
  { text: 'Lead enviado al CRM exitosamente.', type: 'success' }
]

let stepIndex = 0
let timer = null

const runSimulation = () => {
  if (stepIndex < processSimulation.length) {
    terminalSteps.value.push({ ...processSimulation[stepIndex], id: Date.now() })
    stepIndex++
    timer = setTimeout(runSimulation, Math.random() * 1200 + 600)
  } else {
    setTimeout(() => {
      terminalSteps.value = []
      stepIndex = 0
      runSimulation()
    }, 4000)
  }
}

onMounted(() => {
  setTimeout(() => { isLoaded.value = true }, 100)
  runSimulation()
})

onUnmounted(() => {
  if (timer) clearTimeout(timer)
})

const openApp = () => {
  window.open('https://app.neurals.pro', '_blank')
}

// --- NUEVO: Simulación de Chat Rotativo (Frontend) ---
const activeScenarioIndex = ref(0)
const showUserMsg = ref(false)
const agentTyping = ref(false)
const showAgentMsg = ref(false)

const chatScenarios = [
  {
    user: "Hola, ¿tienen las zapatillas Nike en talla 41 y hacen envíos?",
    agent: "¡Hola Carlos! Sí, tenemos disponibles por $85. Incluyen delivery gratis hoy mismo. ¿Te gustaría apartar tu pedido?",
    leadLabel: "Lead / E-commerce"
  },
  {
    user: "Buenas, ¿tendrán amortiguadores para un Toyota Yaris 2022?",
    agent: "¡Hola Felipe! Sí, los tenemos en marca japonesa por $65 el par con garantía de 1 año. ¿Deseas que reserve la pieza para retiro?",
    leadLabel: "Lead / Autopartes"
  },
  {
    user: "Hola, ¿cuánto cuesta una limpieza dental y tienen cupo el sábado?",
    agent: "¡Hola Maria! Cuesta $35. Nos queda disponibilidad el sábado a las 10:00 AM o 2:30 PM. ¿Cuál horario te queda mejor para agendarte?",
    leadLabel: "Lead / Clínica Médica"
  }
]

const runChatSimulation = async () => {
  // Función auxiliar para pausas
  const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

  while (true) {
    // 1. Limpiar chat
    showUserMsg.value = false
    showAgentMsg.value = false
    agentTyping.value = false
    await wait(600)

    // 2. Entra mensaje del usuario
    showUserMsg.value = true
    await wait(1200)

    // 3. Agente "escribiendo"
    agentTyping.value = true
    await wait(1800)

    // 4. Agente responde
    agentTyping.value = false
    showAgentMsg.value = true

    // 5. Tiempo para leer antes de cambiar al siguiente escenario
    await wait(4500)

    // Cambiar al siguiente escenario (bucle infinito)
    activeScenarioIndex.value = (activeScenarioIndex.value + 1) % chatScenarios.length
  }
}

onMounted(() => {
  setTimeout(() => { isLoaded.value = true }, 100)
  runSimulation() // Tu terminal de fondo actual
  runChatSimulation() // El nuevo bucle del chat frontend
})
</script>

<template>
  <section class="relative bg-neural-bg text-neural-text min-h-screen flex items-center overflow-hidden pt-32 pb-24 border-b border-neural-border">

    <!-- EFECTOS DE FONDO: Deep Tech Glow (Sin Cuadrícula) -->
    <div class="absolute inset-0 bg-neural-bg pointer-events-none"></div>
    <!-- Brillo principal (Esquina superior derecha) -->
    <div class="absolute -top-20 -right-20 w-[45rem] h-[45rem] bg-neural-primary/10 rounded-full blur-[150px] pointer-events-none"></div>
    <!-- Brillo secundario (Abajo a la izquierda) -->
    <div class="absolute -bottom-32 left-10 w-[40rem] h-[40rem] bg-neural-accent/10 rounded-full blur-[150px] pointer-events-none"></div>
    <!-- Resplandor central muy sutil para dar volumen -->
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50rem] h-[30rem] bg-neural-primary/5 rounded-full blur-[150px] pointer-events-none"></div>
    <div class="container mx-auto px-6 lg:px-12 relative z-10">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

        <!-- ================= COLUMNA IZQUIERDA: COPY ORIENTADO A PLG (Product-Led Growth) ================= -->
        <div class="space-y-8 relative z-20">

          <!-- Titular Principal con Gradiente Animado (Flow) -->
          <h1
            class="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] transition-all duration-700 delay-100 transform"
            :class="isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'"
          >
            Vende en piloto automático con
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-neural-primary via-neural-accent to-neural-primary bg-[length:200%_auto] animate-gradient-flow">
              tu propio agente.
            </span>
          </h1>

          <!-- Subtítulo -->
          <p
            class="text-lg text-neural-muted font-medium leading-relaxed max-w-xl transition-all duration-700 delay-200 transform"
            :class="isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'"
          >
            Conecta tu catálogo a WhatsApp en minutos. Cotiza sin errores, extrae datos de prospectos en tiempo real y gestiona múltiples líneas desde un panel unificado.
          </p>

          <!-- Botones de Acción (Directos a la App) -->
          <div
            class="flex flex-col sm:flex-row items-center gap-5 pt-2 transition-all duration-700 delay-300 transform"
            :class="isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'"
          >
            <div class="w-full sm:w-auto relative group">
              <!-- Glow trasero del botón -->
              <div class="absolute -inset-0.5 bg-gradient-to-r from-neural-primary to-neural-accent rounded-xl blur opacity-40 group-hover:opacity-75 transition duration-300"></div>
              <button
                @click="openApp"
                class="relative w-full sm:w-auto px-8 py-4 rounded-xl bg-neural-primary hover:bg-neural-primary-hover text-white font-bold text-sm tracking-wide transition-all flex items-center justify-center gap-2"
              >
                <span>Crear Cuenta Gratis</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
              </button>
            </div>

            <a href="#capacidades" class="text-sm font-semibold text-neural-muted hover:text-neural-text transition-colors flex items-center gap-2">
              Explorar capacidades
            </a>
          </div>

          <!-- Micro-texto de confianza para romper la fricción -->
          <div
            class="flex items-center gap-3 pt-2 text-xs font-semibold text-neural-muted transition-all duration-700 delay-500 transform"
            :class="isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'"
          >
            <span class="flex items-center gap-1.5"><svg class="w-3.5 h-3.5 text-neural-online" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg> 3 Días de Prueba</span>
            <span class="w-1 h-1 rounded-full bg-neural-border"></span>
            <span class="flex items-center gap-1.5"><svg class="w-3.5 h-3.5 text-neural-online" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg> Sin Tarjeta de Crédito</span>
          </div>

        </div>

        <!-- ================= COLUMNA DERECHA: COMPOSICIÓN MULTICAPA (Backend + Frontend) ================= -->
        <div
          class="relative w-full h-[450px] transition-all duration-1000 delay-300 transform perspective-1000"
          :class="isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'"
        >

          <!-- 1. CAPA TRASERA: La Terminal (El cerebro) -->
          <div class="absolute top-0 right-0 w-[90%] h-[380px] bg-[#09090b]/80 backdrop-blur-md border border-neural-border rounded-2xl shadow-2xl overflow-hidden flex flex-col transform rotate-y-[-5deg] rotate-x-[2deg]">
            <div class="bg-[#131316] border-b border-neural-border p-3 flex items-center justify-between">
              <div class="flex gap-1.5">
                <div class="w-2.5 h-2.5 rounded-full bg-neural-offline"></div>
                <div class="w-2.5 h-2.5 rounded-full bg-neural-pending"></div>
                <div class="w-2.5 h-2.5 rounded-full bg-neural-online"></div>
              </div>
              <span class="text-[10px] font-mono text-neural-muted">neurals-engine</span>
            </div>
            <div class="flex-1 p-4 overflow-hidden flex flex-col justify-end font-mono text-xs">
              <TransitionGroup name="console" tag="div" class="space-y-2.5">
                <div v-for="step in terminalSteps" :key="step.id" class="flex items-start gap-2.5">
                  <span class="text-neural-muted mt-0.5">❯</span>
                  <span :class="{
                    'text-neural-muted': step.type === 'system',
                    'text-neural-online': step.type === 'success',
                    'text-neural-pending': step.type === 'alert',
                    'text-neural-accent': step.type === 'process',
                    'text-neural-primary': step.type === 'extract',
                  }">{{ step.text }}</span>
                </div>
              </TransitionGroup>
            </div>
          </div>

          <!-- 2. CAPA FRONTAL: Simulador de Chat Animado (Lo que ve el cliente) -->
          <div class="absolute bottom-0 left-0 w-[75%] bg-neural-card/90 backdrop-blur-xl border border-neural-border rounded-2xl shadow-[0_25px_50px_rgba(0,0,0,0.5)] p-5 transform translate-y-4 animate-[float_5s_ease-in-out_infinite]">
            <div class="flex items-center gap-3 mb-4 border-b border-neural-border pb-3">
              <div class="w-8 h-8 rounded-full bg-gradient-to-tr from-neural-primary to-neural-accent flex items-center justify-center shadow-glow-primary">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              </div>
              <div>
                <p class="text-xs font-bold text-neural-text">Neurals Agent</p>
                <!-- Estado dinámico: En línea o Escribiendo -->
                <p class="text-[10px] transition-colors duration-300" :class="agentTyping ? 'text-neural-primary' : 'text-neural-online'">
                  {{ agentTyping ? 'Escribiendo...' : 'En línea' }}
                </p>
              </div>
            </div>

            <div class="space-y-3 min-h-[90px]">

              <!-- Mensaje del usuario (Animado) -->
              <Transition name="chat-bubble">
                <div v-if="showUserMsg" class="flex justify-end">
                  <div class="bg-[#27272A] text-neural-text text-xs p-3 rounded-2xl rounded-tr-none max-w-[85%] shadow-sm">
                    {{ chatScenarios[activeScenarioIndex].user }}
                  </div>
                </div>
              </Transition>

              <!-- Indicador de Escribiendo (3 Puntos) -->
              <Transition name="chat-bubble">
                <div v-if="agentTyping" class="flex justify-start">
                  <div class="bg-neural-primary/10 border border-neural-primary/20 p-3 rounded-2xl rounded-tl-none flex gap-1 items-center shadow-sm">
                    <span class="w-1.5 h-1.5 bg-neural-primary rounded-full animate-bounce"></span>
                    <span class="w-1.5 h-1.5 bg-neural-primary rounded-full animate-bounce [animation-delay:0.2s]"></span>
                    <span class="w-1.5 h-1.5 bg-neural-primary rounded-full animate-bounce [animation-delay:0.4s]"></span>
                  </div>
                </div>
              </Transition>

              <!-- Respuesta del agente cognitivo (Animada) -->
              <Transition name="chat-bubble">
                <div v-if="showAgentMsg" class="flex justify-start">
                  <div class="bg-neural-primary/10 border border-neural-primary/20 text-neural-text text-xs p-3 rounded-2xl rounded-tl-none max-w-[95%] shadow-sm">
                    {{ chatScenarios[activeScenarioIndex].agent }}
                  </div>
                </div>
              </Transition>

            </div>
          </div>

          <!-- 3. ETIQUETA FLOTANTE: Captura de Lead (Actualizada dinámicamente) -->
          <div class="absolute top-12 -left-4 bg-neural-online/10 border border-neural-online/30 rounded-xl py-2 px-4 shadow-xl flex items-center gap-3 backdrop-blur-md animate-[float_4s_ease-in-out_infinite_reverse]">
            <span class="relative flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-neural-online opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-neural-online"></span>
            </span>
            <div class="transition-all duration-500">
              <p class="text-[9px] text-neural-online font-bold uppercase tracking-wider">Lead Cualificado</p>
              <!-- Cambia según la industria actual -->
              <p class="text-xs font-semibold text-neural-text">
                {{ chatScenarios[activeScenarioIndex].leadLabel.split(' / ')[1] }}
              </p>
            </div>
          </div>

        </div>

      </div>
    </div>
  </section>
</template>

<style scoped>
/* Animación del texto para darle vida al SaaS */
@keyframes gradient-flow {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
.animate-gradient-flow {
  animation: gradient-flow 4s ease infinite;
}

/* Transición de la consola */
.console-enter-active,
.console-leave-active {
  transition: all 0.3s ease;
}
.console-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.console-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Animación de flotación suave 3D */
@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-10px) scale(1.02); }
}
.perspective-1000 {
  perspective: 1000px;
}
</style>